import { IconButton } from '@plato/ui/candidates/icon-button';
import type { ButtonIntent } from '@plato/ui/primitives/button';
import { InputGroup, InputGroupTextarea } from '@plato/ui/primitives/input-group';
import { cn } from '@plato/ui/util/classes';
import { ArrowUpIcon } from 'lucide-react';
import type { ComponentPropsWithRef, ReactElement } from 'react';

export type PromptInputProps = ComponentPropsWithRef<'form'> & {
    /**
     * The whole composer is off, e.g. the conversation is closed. The surface
     * takes the disabled look; without it only the buttons inside ever do.
     */
    disabled?: boolean;
};

export function PromptInput({ className, disabled = false, ...props }: PromptInputProps): ReactElement {
    return (
        <InputGroup
            className={cn(
                'rounded-default',
                !disabled && [
                    'state-disabled-within:cursor-auto state-disabled-within:border-line',
                    'state-disabled-within:bg-transparent state-disabled-within:text-inherit',
                ],
                className,
            )}
            render={<form {...props} />}
        />
    );
}

export type PromptInputTextareaProps = ComponentPropsWithRef<'textarea'>;

export function PromptInputTextarea({
    className,
    onKeyDown,
    placeholder = 'Ask anything',
    ...props
}: PromptInputTextareaProps): ReactElement {
    return (
        <InputGroupTextarea
            className={cn('field-sizing-content max-h-40 placeholder:truncate', className)}
            placeholder={placeholder}
            onKeyDown={(event) => {
                onKeyDown?.(event);
                if (
                    event.key === 'Enter' &&
                    !event.shiftKey &&
                    !event.nativeEvent.isComposing &&
                    !event.defaultPrevented
                ) {
                    event.preventDefault();
                    event.currentTarget.form?.requestSubmit();
                }
            }}
            {...props}
        />
    );
}

export type PromptInputSubmitProps = ComponentPropsWithRef<'button'> & {
    label?: string;
    intent?: ButtonIntent;
};

export function PromptInputSubmit({
    className,
    intent,
    label = 'Send',
    ...props
}: PromptInputSubmitProps): ReactElement {
    return (
        <IconButton
            type="submit"
            variant="primary"
            intent={intent}
            label={label}
            // The composer is the panel's one primary action, so it keeps its full
            // fill instead of washing out inside nested containers.
            className={cn(
                'l2l3:bg-(--c-color-main) l2l3:text-(--c-color-contrast)',
                'l2l3:hover:bg-[color-mix(in_oklch,var(--c-color-main)_var(--tint-hover),var(--tint-base))]',
                className,
            )}
            {...props}
        >
            <ArrowUpIcon />
        </IconButton>
    );
}
